/**
 * Multi-Chat Page
 * Multi-Chat System: Main chat interface with dual sidebars
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Tooltip,
  IconButton,
  Fab,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ListAlt as ListAltIcon,
  DeleteSweep as DeleteSweepIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useSnackbar } from '@/contexts/SnackbarContext';
import { useDialogState } from '@/hooks/ui/useDialogState';

// Chat components
import {
  ChatMessage,
  ChatInput,
  DeleteConfirmDialog,
} from '../components/chat';

// Conversation components
import {
  ConversationSidebar,
  DocumentReferencesSidebar,
} from '../components/conversation';

// API hooks
import {
  useGetConversation,
  useCreateConversation,
  useClearConversationMessages,
  useGenerateConversationTitle,
} from '../hooks/api/useConversations';
import { useSendMessage } from '../hooks/api/useConversationChat';

// State management
import { useConversationStore } from '../stores/conversationStore';
import { log } from '../utils/errors/logger';
import { useSystem } from '../hooks/api/config/useSystemOrganization';
import type { ChatSource } from '../api/ApiClient';

/**
 * Helper to get appropriate error message for API errors
 */
type ChatError = {
  status?: number;
  code?: string;
  message?: string;
};

const getErrorMessage = (error: unknown, t: TFunction): string => {
  const chatError = (error as ChatError) ?? {};
  const isQuotaError =
    chatError.status === 429 || chatError.code === 'QUOTA_EXCEEDED';
  return isQuotaError ? t('common.errors.quotaExceeded') : t('chat.errorMessage');
};

const MultiChat: React.FC = () => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useSnackbar();
  const dialogState = useDialogState<{ id?: string }>();
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const bottomAnchorRef = useRef<HTMLDivElement>(null);
  const shouldAutoScrollRef = useRef(true);
  const scrollRafRef = useRef<number | null>(null);
  const navigate = useNavigate();
  const { system } = useSystem();

  // Get conversation ID from URL params
  const { conversationId: currentConversationId } = useParams<{ conversationId?: string }>();

  // State management - UI preferences only
  const {
    conversationSidebarOpen,
    documentReferencesSidebarOpen,
    toggleConversationSidebar,
    toggleDocumentReferencesSidebar,
    setConversationSidebarOpen,
  } = useConversationStore();

  // Local state
  const [input, setInput] = useState('');
  const [streamingMessage, setStreamingMessage] = useState<string>('');
  const [streamingConversationId, setStreamingConversationId] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [showScrollToLatest, setShowScrollToLatest] = useState(false);

  // API hooks
  const { data: currentConversation, isLoading: conversationLoading, error: conversationError } = useGetConversation(currentConversationId || null, {
    enabled: Boolean(currentConversationId),
  });
  const createConversationMutation = useCreateConversation();
  const sendMessageMutation = useSendMessage();
  const clearMessagesMutation = useClearConversationMessages();
  const generateTitleMutation = useGenerateConversationTitle();
  const messageCount = Array.isArray(currentConversation?.messages)
    ? currentConversation.messages.length
    : 0;

  const isNearBottom = (element: HTMLDivElement, thresholdPx = 120): boolean => {
    const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
    return distanceFromBottom <= thresholdPx;
  };

  const scrollToBottomInstant = () => {
    const container = messagesContainerRef.current;
    if (!container) {return;}
    container.scrollTop = container.scrollHeight;
    setShowScrollToLatest(false);
  };

  const scheduleAutoScroll = () => {
    if (!shouldAutoScrollRef.current || scrollRafRef.current !== null) {return;}

    scrollRafRef.current = window.requestAnimationFrame(() => {
      scrollRafRef.current = null;
      scrollToBottomInstant();
    });
  };

  useEffect(() => {
    return () => {
      if (scrollRafRef.current !== null) {
        window.cancelAnimationFrame(scrollRafRef.current);
      }
    };
  }, []);

  useEffect(() => {
    shouldAutoScrollRef.current = true;
  }, [currentConversationId]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) {return;}

    const handleScroll = () => {
      const nearBottom = isNearBottom(container);
      shouldAutoScrollRef.current = nearBottom;
      setShowScrollToLatest(!nearBottom && messageCount > 0);
    };

    handleScroll();
    container.addEventListener('scroll', handleScroll);
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [currentConversationId, messageCount]);

  useEffect(() => {
    scheduleAutoScroll();
  }, [currentConversation?.messages]);

  // Ensure streaming chunks scroll after they are rendered in the DOM.
  useEffect(() => {
    if (isStreaming && streamingMessage) {
      scheduleAutoScroll();
    }
  }, [isStreaming, scheduleAutoScroll, streamingMessage]);

  // Handle invalid conversation ID
  useEffect(() => {
    // Conversation ID exists but conversation not found (404 error)
    // This can happen if conversation was deleted on backend
    if (currentConversationId && conversationError && !conversationLoading && !createConversationMutation.isPending) {
      log.warn('Conversation not found, navigating to chat home', {
        invalidConversationId: currentConversationId,
        error: conversationError
      });

      // Navigate to chat home (no conversation ID)
      void navigate('/chat', { replace: true });
      showError(t('conversation.notFound', { defaultValue: 'Conversation not found' }));
    }
  }, [currentConversationId, conversationLoading, conversationError, createConversationMutation.isPending, navigate, showError, t]);

  // Event handlers
  const handleSend = async () => {
    if (!input.trim()) {return;}

    const messageContent = input.trim();
    let conversationIdToUse = currentConversationId;
    
    // If no current conversation, create one first
    if (!conversationIdToUse) {
      try {
        log.info('Creating new conversation for first message');
        const newConversation = await createConversationMutation.mutateAsync(undefined);
        conversationIdToUse = newConversation.id;

        // Navigate to the new conversation
        void navigate(`/chat/${newConversation.id}`, { replace: true });
        // Continue with sending the message after creating conversation
      } catch (error) {
        log.error('Failed to create conversation before sending message', error);
        showError(t('conversation.createFailed'));
        return;
      }
    }

    setInput('');
    setIsStreaming(true);
    setStreamingMessage('');
    setStreamingConversationId(conversationIdToUse ?? null);
    let messageDelivered = false;

    // Sending a user message should always keep latest content visible.
    shouldAutoScrollRef.current = true;
    scheduleAutoScroll();

    try {
      log.user.action('send_message', 'MultiChat', { 
        conversationId: conversationIdToUse,
        contentLength: messageContent.length 
      });
      
      await sendMessageMutation.mutateAsync({
        conversationId: conversationIdToUse,
        content: messageContent,
        onChunk: (chunk: string) => {
          if (chunk) {
            // Update streaming message with new chunk and follow only when user stays near bottom.
            setStreamingMessage(prev => prev + chunk);
            scheduleAutoScroll();
          }
          // Context is handled by the mutation automatically
        }
      });
      messageDelivered = true;

      // Auto-generate title if this is the first message
      if (!currentConversation || currentConversation?.message_count === 0) {
        setTimeout(() => {
          void generateTitleMutation.mutate(conversationIdToUse);
        }, 1000); // Delay to ensure message is saved
      }
    } catch (error: unknown) {
      log.error('Failed to send message', error);
      
      // Check if it's a 404 error (conversation doesn't exist)
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('404') || errorMessage.includes('Not Found')) {
        log.warn('Conversation not found during send, creating new conversation', {
          invalidConversationId: currentConversationId
        });

        try {
          // Create new conversation and retry sending
          const newConversation = await createConversationMutation.mutateAsync(undefined);

          // Navigate to the new conversation
          void navigate(`/chat/${newConversation.id}`, { replace: true });

          // Retry sending the message to the new conversation
          await sendMessageMutation.mutateAsync({
            conversationId: newConversation.id,
            content: messageContent,
            onChunk: (chunk: string) => {
              if (chunk) {
                setStreamingMessage(prev => prev + chunk);
                scheduleAutoScroll();
              }
            }
          });
          messageDelivered = true;

          showSuccess(t('conversation.created', { title: newConversation.title }));
        } catch (retryError) {
          log.error('Failed to create new conversation and retry message', retryError);
          showError(getErrorMessage(retryError, t));
          setInput(messageContent); // Restore input on error
        }
      } else {
        showError(getErrorMessage(error, t));
        setInput(messageContent); // Restore input on error
      }
    } finally {
      setIsStreaming(false);
      if (!messageDelivered) {
        setStreamingMessage('');
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  const handleClearConversation = async () => {
    if (!currentConversationId) {return;}

    try {
      await clearMessagesMutation.mutateAsync(currentConversationId);
      showSuccess(t('conversation.messagesCleared'));
      dialogState.closeDeleteConfirm();
      
      log.user.action('clear_conversation', 'MultiChat', { conversationId: currentConversationId });
    } catch (error) {
      log.error('Failed to clear conversation', error);
      showError(t('conversation.clearFailed'));
    }
  };

  // Component logging
  useEffect(() => {
    log.component.mount('MultiChat', { currentConversationId });
  }, []);

  const messages = Array.isArray(currentConversation?.messages)
    ? currentConversation.messages
      .filter((message): message is { id?: string; role: string; content: string; sources?: unknown[] } =>
        typeof (message as { role?: unknown }).role === 'string' &&
        typeof (message as { content?: unknown }).content === 'string'
      )
      .map(message => ({
        ...message,
        sources: Array.isArray(message.sources) ? (message.sources as ChatSource[]) : undefined,
      }))
    : [];
  const isStreamingForCurrentConversation =
    streamingConversationId !== null && streamingConversationId === currentConversationId;
  const hasPersistedStreamingMessage = Boolean(
    streamingMessage &&
    messages.some((message) => message.role === 'assistant' && message.content === streamingMessage)
  );
  const shouldRenderStreamingMessage =
    isStreamingForCurrentConversation && Boolean(streamingMessage) && !hasPersistedStreamingMessage;
  const isLoading = conversationLoading || sendMessageMutation.isPending;

  useEffect(() => {
    if (hasPersistedStreamingMessage) {
      setStreamingMessage('');
    }
  }, [hasPersistedStreamingMessage]);

  return (
    <Box
      id="multi-chat-container"
      sx={{
        height: 'calc(100vh - 64px)',
        width: '100%',
        display: 'flex',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Left: Conversation Sidebar */}
      <Box
        id="conversation-sidebar-container"
        sx={{
          width: conversationSidebarOpen ? { xs: 260, md: 280, lg: 300 } : 0,
          flexShrink: 0,
          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden',
          borderRight: conversationSidebarOpen ? 1 : 0,
          borderColor: 'divider',
        }}
      >
        <ConversationSidebar
          open={conversationSidebarOpen}
          onClose={() => setConversationSidebarOpen(false)}
        />
      </Box>
      
      {/* Center: Main Chat Area */}
      <Box 
        id="main-chat-area"
        component="main" 
        sx={{ 
          flexGrow: 1, 
          display: 'flex', 
          flexDirection: 'column', 
          overflow: 'hidden',
          position: 'relative',
          minWidth: 0, // Prevents flex item from overflowing
        }}
      >
        {/* Messages Area */}
        <Box
          id="chat-messages-area"
          ref={messagesContainerRef}
          role="log"
          aria-live="polite"
          aria-label={t('chat.messagesArea', { defaultValue: 'Chat messages' })}
          sx={{
            flexGrow: 1,
            overflow: 'auto',
            p: { xs: 1, sm: 1.5, md: 2 },
            bgcolor: '#f8f8f8',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Empty State */}
          {messages.length === 0 && !conversationLoading && (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '100%',
              p: 3 
            }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: '#003064',
                  mb: 2,
                  fontWeight: 500
                }} 
                align="center"
              >
                {t('chat.welcome', { systemName: system?.name || 'smartDoc' })}
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ color: '#666666' }} 
                align="center"
              >
                {t('chat.askAnything')}
              </Typography>
            </Box>
          )}

          {/* Messages */}
              {messages.map((message, index) => (
                <ChatMessage
                  key={message.id || index}
                  message={message}
                  isUser={message.role === 'user'}
                />
              ))}

          {/* Streaming Message */}
          {shouldRenderStreamingMessage && (
            <ChatMessage
              key="streaming-message"
              message={{
                role: 'assistant',
                content: streamingMessage,
                sources: []
              }}
              isUser={false}
            />
          )}

          {/* Loading Indicator */}
          {(isLoading || (isStreaming && isStreamingForCurrentConversation)) && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 1,
                my: 2,
                minHeight: 32,
              }}
            >
              {(isLoading || (isStreaming && isStreamingForCurrentConversation && !streamingMessage)) && (
                <CircularProgress size={24} sx={{ color: '#003064' }} />
              )}
              <Typography variant="caption" color="text.secondary">
                {t('chat.generating', { defaultValue: 'Generating response...' })}
              </Typography>
            </Box>
          )}

          <div ref={bottomAnchorRef} />
        </Box>

        {showScrollToLatest && (
          <Box
            sx={{
              position: 'absolute',
              right: 24,
              bottom: 126,
              zIndex: 5,
            }}
          >
            <Tooltip title={t('chat.scrollToLatest', { defaultValue: 'Scroll to latest message' })} arrow>
              <Fab
                size="small"
                color="primary"
                onClick={scrollToBottomInstant}
                aria-label={t('chat.scrollToLatest', { defaultValue: 'Scroll to latest message' })}
              >
                <KeyboardArrowDownIcon />
              </Fab>
            </Tooltip>
          </Box>
        )}

        {/* Fixed Bottom Input Area */}
        <Box
          id="chat-input-area"
          sx={{
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            p: 1.5,
          }}
        >
          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              justifyContent: 'space-between',
              mb: 1,
              pb: 1,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            {/* Left side buttons */}
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <Tooltip title={t('conversation.conversations')} arrow>
                <IconButton
                  onClick={toggleConversationSidebar}
                  color={conversationSidebarOpen ? 'primary' : 'default'}
                  sx={{
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.1)',
                      bgcolor: conversationSidebarOpen ? 'primary.lighter' : 'action.hover',
                    },
                  }}
                >
                  <MenuIcon />
                </IconButton>
              </Tooltip>

              {/* TODO: Commented out new chat button - new chats can be created via conversation sidebar */}
              {/* <Tooltip title={t('conversation.new')} arrow>
                <span>
                  <Fab
                    size="small"
                    color="primary"
                    onClick={() => { void handleCreateNewChat(); }}
                    disabled={createConversationMutation.isPending}
                    sx={{
                      boxShadow: 2,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 4,
                      },
                      '&:active': {
                        transform: 'translateY(0)',
                      },
                    }}
                  >
                    <AddIcon />
                  </Fab>
                </span>
              </Tooltip> */}

              {/* TODO: Commented out manual title generation button - auto-generation still works on first message */}
              {/* {currentConversationId && messages.length > 0 && (
                <Tooltip title={t('conversation.generateTitle')} arrow>
                  <span>
                    <Fab
                      size="small"
                      color="secondary"
                      onClick={() => generateTitleMutation.mutate(currentConversationId)}
                      disabled={generateTitleMutation.isPending}
                      sx={{
                        boxShadow: 2,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: 4,
                        },
                        '&:active': {
                          transform: 'translateY(0)',
                        },
                      }}
                    >
                      <AutoFixHighIcon />
                    </Fab>
                  </span>
                </Tooltip>
              )} */}
            </Box>

            {/* Right side buttons */}
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <Tooltip title={t('conversation.documentReferences')} arrow>
                <IconButton
                  onClick={toggleDocumentReferencesSidebar}
                  color={documentReferencesSidebarOpen ? 'primary' : 'default'}
                  sx={{
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.1)',
                      bgcolor: documentReferencesSidebarOpen ? 'primary.lighter' : 'action.hover',
                    },
                  }}
                >
                  <ListAltIcon />
                </IconButton>
              </Tooltip>

              {messages.length > 0 && (
                <Tooltip title={t('chat.clearHistory')} arrow>
                  <IconButton
                    onClick={() => dialogState.openDeleteConfirm({ id: currentConversationId })}
                    color="error"
                    disabled={clearMessagesMutation.isPending}
                    sx={{
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'scale(1.1)',
                        bgcolor: 'error.lighter',
                      },
                    }}
                  >
                    <DeleteSweepIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>

          {/* Chat Input */}
          <ChatInput
            input={input}
            loading={isLoading}
            onInputChange={setInput}
            onSend={() => { void handleSend(); }}
            onKeyDown={handleKeyDown}
          />
        </Box>
      </Box>

      {/* Right: Document References Sidebar */}
      <Box
        id="document-references-sidebar-container"
        sx={{
          width: documentReferencesSidebarOpen ? { xs: 280, md: 300, lg: 320 } : 0,
          flexShrink: 0,
          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden',
          borderLeft: documentReferencesSidebarOpen ? 1 : 0,
          borderColor: 'divider',
        }}
      >
        <DocumentReferencesSidebar
          open={documentReferencesSidebarOpen}
          onClose={() => useConversationStore.setState({ documentReferencesSidebarOpen: false })}
        />
      </Box>
      
      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={dialogState.deleteConfirmOpen}
        loading={clearMessagesMutation.isPending}
        onClose={dialogState.closeDeleteConfirm}
        onConfirm={() => { void handleClearConversation(); }}
        title={t('chat.deleteConfirm.title')}
        message={t('chat.deleteConfirm.message')}
        confirmLabel={t('chat.deleteConfirm.confirm')}
        cancelLabel={t('chat.deleteConfirm.cancel')}
        showDeleteIcon
      />
    </Box>
  );
};

export default MultiChat;
